import type { Config, OverridedMixpanel } from "mixpanel-browser";
// The session-replay recorder, served from our own origin. The SDK's own
// default is the unminified build from Mixpanel's CDN, nearly 1 MB and a host
// ad blockers know. The file name is the SDK release's: after upgrading
// mixpanel-browser, take the new name from its dist/async-modules (the build
// fails until then, which is the point).
import recorderSrc from "mixpanel-browser/dist/async-modules/mixpanel-recorder-CcIR1Do8.min.js?url";
import { redactProperties } from "@/lib/status-token";

/**
 * Mixpanel, for product analytics: where visitors land, what they click, where
 * they get stuck (DEL-442). The ad platforms get the funnel for their bidding
 * and our own orders decide attribution (DEL-433); Mixpanel is for watching
 * the store being used.
 *
 * One project, Kanata's production one, by choice: there is no dev project.
 * Every event carries `environment` instead, so staging and local traffic can
 * be filtered out of a report. Pages don't call this file: lib/tracking.ts
 * does, alongside the Pixel and the Google tag.
 */

/** Kanata's project token. Not a secret: like the Pixel ID, it ships in every page. */
export const MIXPANEL_TOKEN = "913f3396cdd38bd1a88800d40f644d78";

export type MixpanelEnvironment = "production" | "staging" | "local";

const PRODUCTION_HOSTS = new Set(["kanataindonesia.com", "www.kanataindonesia.com"]);
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

/**
 * Which environment a host reports as, or null for one that stays silent.
 *
 * dev.kanataindonesia.com and the Worker's own previews report as staging, so
 * the store can be checked in Live View before launch. A local dev server
 * reports only when VITE_MIXPANEL_LOCAL=1, or every `pnpm dev` would show up.
 */
export function mixpanelEnvironment(hostname: string, localOptIn: boolean): MixpanelEnvironment | null {
  const h = hostname.toLowerCase();
  if (PRODUCTION_HOSTS.has(h)) return "production";
  if (h === "dev.kanataindonesia.com" || h.endsWith(".workers.dev")) return "staging";
  if (LOCAL_HOSTS.has(h) && localOptIn) return "local";
  return null;
}

/**
 * Where the SDK sends events. The deployed storefront goes through its own
 * Worker (/mp, worker/index.ts), because ad blockers drop requests to
 * mixpanel.com and the paid traffic we most want to see is the traffic most
 * likely to run one. A local dev server has no Worker, so it talks to
 * Mixpanel directly.
 */
export function mixpanelApiHost(env: MixpanelEnvironment, origin: string): string {
  return env === "local" ? "https://api-js.mixpanel.com" : `${origin}/mp`;
}

/**
 * Clicks (with their text, so a report says "Tambah ke keranjang", not
 * "button"), rage and dead clicks, scroll depth, and a page view on every
 * route change. Query strings count as a new page, so the catalog's ?jenis=
 * filters show up.
 */
const AUTOCAPTURE: Config["autocapture"] = {
  pageview: "url-with-path-and-query-string",
  capture_text_content: true,
  block_selectors: ["[data-private]"],
};

function config(env: MixpanelEnvironment, origin: string): Partial<Config> {
  return {
    api_host: mixpanelApiHost(env, origin),
    recorder_src: recorderSrc,
    debug: env === "local",
    persistence: "localStorage",
    // Off for init; switched on once `environment` is registered.
    autocapture: false,
    // One session in five (4 Nov; it was every session while traffic was
    // small). Enough replays to watch, at a fifth of the recorder's cost to
    // visitors. Inputs are masked (names, phone numbers, addresses); page
    // text is not, or the replay would be a page of asterisks. Mark anything
    // personal that is displayed, not typed, with data-private. Images are
    // product photos, so they stay visible.
    record_sessions_percent: 20,
    record_mask_all_inputs: true,
    record_mask_all_text: false,
    record_mask_text_selector: ["[data-private]"],
    record_block_selector: "video, audio",
    record_heatmap_data: true,
    // The payment page's URL carries the buyer's status token, and every
    // event on that page carries the URL. Session replays don't pass through
    // this hook: the Worker redacts those on the way through /mp.
    hooks: {
      before_send_events: (e) => ({ ...e, properties: redactProperties(e.properties) }),
    },
  };
}

let client: Promise<OverridedMixpanel | null> | null = null;

/**
 * The SDK, loaded once and only where it reports. A dynamic import keeps it
 * out of the first page load; the replay recorder is a second bundle the SDK
 * fetches when a recording starts. Resolves to null where Mixpanel stays
 * silent.
 */
function currentEnvironment(): MixpanelEnvironment | null {
  if (typeof window === "undefined") return null;
  return mixpanelEnvironment(window.location.hostname, import.meta.env.VITE_MIXPANEL_LOCAL === "1");
}

export function loadMixpanel(): Promise<OverridedMixpanel | null> {
  const env = currentEnvironment();
  if (!env) return Promise.resolve(null);
  // The prebuilt bundle, not the package's src/ loaders: those leave the
  // recorder's file name as an unfilled placeholder.
  client ??= import("mixpanel-browser/dist/mixpanel-with-async-modules.cjs.js")
    .then(({ default: mixpanel }) => {
      mixpanel.init(MIXPANEL_TOKEN, config(env, window.location.origin));
      // Autocapture sends the first page view the moment it starts, so it
      // starts after `environment` is registered, or that page view would go
      // out untagged.
      mixpanel.register({ environment: env });
      mixpanel.set_config({ autocapture: AUTOCAPTURE });
      return mixpanel;
    })
    .catch(() => null);
  return client;
}

/**
 * What checkout sends so the server can report order_paid (DEL-444): this
 * browser's device, so the sale joins its funnel, and the environment it was
 * placed in. Nothing where Mixpanel is off, so the server stays quiet too. A
 * browser that blocked the SDK still sends the environment: the sale counts,
 * just without its visit.
 */
export async function mixpanelForCheckout(): Promise<{ deviceId?: string; environment: string } | undefined> {
  const environment = currentEnvironment();
  if (!environment) return undefined;
  const mp = await loadMixpanel();
  const deviceId = mp?.get_property("$device_id");
  return { environment, deviceId: typeof deviceId === "string" && deviceId ? deviceId : undefined };
}

/**
 * One event. Properties are snake_case, numbers are numbers, and nothing that
 * names a person goes in: no name, phone, email or address.
 */
export function trackMixpanel(event: string, props: MixpanelProps): void {
  const clean = withoutBlanks(props);
  void loadMixpanel().then((mp) => mp?.track(event, clean));
}

export type MixpanelProps = Record<string, string | number | boolean | undefined>;

/**
 * The properties that have a value. A property that doesn't apply is left
 * out, not sent empty: Mixpanel would count "" as a value of its own.
 */
export function withoutBlanks(props: MixpanelProps): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(props).filter((e): e is [string, string | number | boolean] => e[1] !== undefined && e[1] !== ""),
  );
}
